import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useTenant } from "../lib/tenant-context";
import { StartProcessingButton } from "../components/warehouse/StartProcessingButton";

interface StageTransition {
  id: string;
  from_stage: string | null;
  to_stage: string;
  transitioned_at: string;
  operator_id: string | null;
  notes: string | null;
}

interface WarehousePackage {
  id: string;
  bag_label_id: string;
  tenant_id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  weight_grams: number | null;
  estimated_pod_count: number | null;
  actual_pod_count: number | null;
  current_stage: string;
  kiosk_location: string | null;
  received_at: string;
  processing_started_at: string | null;
  processed_at: string | null;
  operator_id: string | null;
  notes: string | null;
  flags: string | null;
  created_at: string;
  updated_at: string;
  transitions?: StageTransition[];
}

interface GradedItem {
  productName: string;
  productImage: string;
  condition: "Excellent" | "Good" | "Fair" | "Poor";
  conditionScore: number;
  grade: string;
  defects: string[];
  estimatedResaleValue: number;
  vlmConfidence: number;
  verdict: "Resale" | "Refurbish" | "Recycle";
}

export const Route = createFileRoute("/warehouse_/package_/$id")({
  component: WarehousePackageDetailPage,
});

// Tenant-aware labels
function getTenantLabels(tenantId: string) {
  const isNespresso = tenantId === "nespresso";
  return {
    estimatedCount: isNespresso ? "Estimated Pods" : "Est. Items",
    actualCount: isNespresso ? "Actual Pods" : "Actual Items",
    estimatedCountLabel: isNespresso
      ? "Estimated Pod Count"
      : "Est. Item Count",
    actualCountLabel: isNespresso ? "Actual Pod Count" : "Actual Item Count",
    locationLabel: isNespresso ? "Kiosk Location" : "Drop-off Location",
  };
}

// Demo package data keyed by ID for detail page fallback
interface DemoListPackage {
  id: string;
  bagLabelId: string;
  weight: number;
  estimatedPodCount: number;
  actualPodCount: number | null;
  stage: string;
  customerName: string;
  customerEmail: string;
  customerTier: string;
  kioskLocation: string;
  kioskCity: string;
  receivedAt: string;
  processingStartedAt: string | null;
  processedAt: string | null;
  hasFlag: boolean;
  flagNote: string | null;
}

function buildLookup(
  packages: DemoListPackage[],
  tenantId: string,
): Record<string, WarehousePackage> {
  const lookup: Record<string, WarehousePackage> = {};
  for (const p of packages) {
    const transitions: StageTransition[] = [
      {
        id: `${p.id}_t1`,
        from_stage: null,
        to_stage: "received",
        transitioned_at: p.receivedAt,
        operator_id: null,
        notes: "Package received at warehouse",
      },
    ];
    if (p.processingStartedAt) {
      transitions.push({
        id: `${p.id}_t2`,
        from_stage: "received",
        to_stage: "processing",
        transitioned_at: p.processingStartedAt,
        operator_id: "warehouse_op_01",
        notes: "Processing started",
      });
    }
    if (p.processedAt) {
      transitions.push({
        id: `${p.id}_t3`,
        from_stage: "processing",
        to_stage: "processed",
        transitioned_at: p.processedAt,
        operator_id: "warehouse_op_01",
        notes: "Processing complete",
      });
    }
    lookup[p.id] = {
      id: p.id,
      bag_label_id: p.bagLabelId,
      tenant_id: tenantId,
      customer_id: `cust_${p.id}`,
      customer_name: p.customerName,
      customer_email: p.customerEmail,
      weight_grams: Math.round(p.weight * 1000),
      estimated_pod_count: p.estimatedPodCount,
      actual_pod_count: p.actualPodCount,
      current_stage: p.stage,
      kiosk_location: p.kioskLocation,
      received_at: p.receivedAt,
      processing_started_at: p.processingStartedAt,
      processed_at: p.processedAt,
      operator_id: p.processingStartedAt ? "warehouse_op_01" : null,
      notes: null,
      flags:
        p.hasFlag && p.flagNote
          ? JSON.stringify([{ type: "warning", note: p.flagNote }])
          : null,
      created_at: p.receivedAt,
      updated_at: p.processedAt || p.processingStartedAt || p.receivedAt,
      transitions,
    };
  }
  return lookup;
}

const NESPRESSO_LIST: DemoListPackage[] = [
  {
    id: "pkg_001",
    bagLabelId: "NES-2025-001234",
    weight: 2.4,
    estimatedPodCount: 48,
    actualPodCount: null,
    stage: "received",
    customerName: "Marie Laurent",
    customerEmail: "marie.laurent@email.com",
    customerTier: "Gold",
    kioskLocation: "Nespresso Boutique Champs-Elysees",
    kioskCity: "Paris",
    receivedAt: "2025-01-22T08:30:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_002",
    bagLabelId: "NES-2025-001235",
    weight: 1.8,
    estimatedPodCount: 36,
    actualPodCount: null,
    stage: "received",
    customerName: "Jean-Pierre Dubois",
    customerEmail: "jp.dubois@email.com",
    customerTier: "Platinum",
    kioskLocation: "Nespresso Boutique Le Marais",
    kioskCity: "Paris",
    receivedAt: "2025-01-22T09:15:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_003",
    bagLabelId: "NES-2025-001230",
    weight: 3.2,
    estimatedPodCount: 64,
    actualPodCount: null,
    stage: "processing",
    customerName: "Sophie Martin",
    customerEmail: "sophie.m@email.com",
    customerTier: "Gold",
    kioskLocation: "Nespresso Boutique Lyon",
    kioskCity: "Lyon",
    receivedAt: "2025-01-21T14:20:00Z",
    processingStartedAt: "2025-01-22T10:00:00Z",
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_005",
    bagLabelId: "NES-2025-001220",
    weight: 2.8,
    estimatedPodCount: 56,
    actualPodCount: 52,
    stage: "processed",
    customerName: "Claire Moreau",
    customerEmail: "claire.moreau@email.com",
    customerTier: "Platinum",
    kioskLocation: "Nespresso Boutique Bordeaux",
    kioskCity: "Bordeaux",
    receivedAt: "2025-01-20T16:00:00Z",
    processingStartedAt: "2025-01-21T08:00:00Z",
    processedAt: "2025-01-21T08:45:00Z",
    hasFlag: false,
    flagNote: null,
  },
];

const ADIDAS_LIST: DemoListPackage[] = [
  {
    id: "adi_pkg_001",
    bagLabelId: "ADI-2026-004501",
    weight: 3.8,
    estimatedPodCount: 6,
    actualPodCount: null,
    stage: "received",
    customerName: "Sarah Chen",
    customerEmail: "s.chen@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Oxford Street",
    kioskCity: "London",
    receivedAt: "2026-02-18T09:30:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_002",
    bagLabelId: "ADI-2026-004502",
    weight: 2.1,
    estimatedPodCount: 3,
    actualPodCount: null,
    stage: "received",
    customerName: "Marcus Johnson",
    customerEmail: "m.johnson@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Brooklyn",
    kioskCity: "New York",
    receivedAt: "2026-02-18T11:15:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_003",
    bagLabelId: "ADI-2026-004498",
    weight: 5.4,
    estimatedPodCount: 8,
    actualPodCount: null,
    stage: "received",
    customerName: "Emma Lindqvist",
    customerEmail: "e.lindqvist@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Herzogenaurach",
    kioskCity: "Herzogenaurach",
    receivedAt: "2026-02-17T14:00:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_004",
    bagLabelId: "ADI-2026-004495",
    weight: 1.9,
    estimatedPodCount: 2,
    actualPodCount: null,
    stage: "processing",
    customerName: "Kenji Tanaka",
    customerEmail: "k.tanaka@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Brand Center Tokyo",
    kioskCity: "Tokyo",
    receivedAt: "2026-02-16T10:20:00Z",
    processingStartedAt: "2026-02-17T08:00:00Z",
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_005",
    bagLabelId: "ADI-2026-004490",
    weight: 4.2,
    estimatedPodCount: 5,
    actualPodCount: null,
    stage: "processing",
    customerName: "Lea Fontaine",
    customerEmail: "l.fontaine@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Champs-Elysees",
    kioskCity: "Paris",
    receivedAt: "2026-02-15T16:45:00Z",
    processingStartedAt: "2026-02-16T09:30:00Z",
    processedAt: null,
    hasFlag: true,
    flagNote: "Contains non-adidas items mixed in",
  },
  {
    id: "adi_pkg_006",
    bagLabelId: "ADI-2026-004482",
    weight: 3.0,
    estimatedPodCount: 4,
    actualPodCount: 4,
    stage: "processed",
    customerName: "Alex Rodriguez",
    customerEmail: "a.rodriguez@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Santa Monica",
    kioskCity: "Los Angeles",
    receivedAt: "2026-02-14T12:00:00Z",
    processingStartedAt: "2026-02-15T08:00:00Z",
    processedAt: "2026-02-15T10:30:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_007",
    bagLabelId: "ADI-2026-004478",
    weight: 6.1,
    estimatedPodCount: 10,
    actualPodCount: 9,
    stage: "processed",
    customerName: "Priya Sharma",
    customerEmail: "p.sharma@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Mumbai",
    kioskCity: "Mumbai",
    receivedAt: "2026-02-13T09:30:00Z",
    processingStartedAt: "2026-02-14T08:00:00Z",
    processedAt: "2026-02-14T11:45:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_008",
    bagLabelId: "ADI-2026-004470",
    weight: 2.5,
    estimatedPodCount: 3,
    actualPodCount: 3,
    stage: "processed",
    customerName: "Jonas Weber",
    customerEmail: "j.weber@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Berlin Mitte",
    kioskCity: "Berlin",
    receivedAt: "2026-02-12T15:20:00Z",
    processingStartedAt: "2026-02-13T09:00:00Z",
    processedAt: "2026-02-13T11:00:00Z",
    hasFlag: false,
    flagNote: null,
  },
];

// VLM grading data for processed Adidas packages
const ADIDAS_GRADED_ITEMS: Record<string, GradedItem[]> = {
  adi_pkg_006: [
    {
      productName: "Ultraboost 22 Running Shoes",
      productImage:
        "/assets/images/products/adidas/Rapidmove_Pro_Training_Shoes_Orange_JS4268_HM1.jpg",
      condition: "Excellent",
      conditionScore: 9.2,
      grade: "A+",
      defects: [],
      estimatedResaleValue: 112,
      vlmConfidence: 0.96,
      verdict: "Resale",
    },
    {
      productName: "Handball Spezial Shoes",
      productImage: "/assets/images/products/adidas/handball-spezial-shoes.jpg",
      condition: "Good",
      conditionScore: 7.5,
      grade: "B+",
      defects: ["Minor sole wear", "Light scuff on toe box"],
      estimatedResaleValue: 68,
      vlmConfidence: 0.91,
      verdict: "Resale",
    },
    {
      productName: "Essentials Glossy Puffer Jacket",
      productImage:
        "/assets/images/products/adidas/essentials-glossy-2-in-1-cropped-puffer.jpg",
      condition: "Good",
      conditionScore: 7.0,
      grade: "B",
      defects: ["Small zipper scratch"],
      estimatedResaleValue: 45,
      vlmConfidence: 0.88,
      verdict: "Resale",
    },
    {
      productName: "Terrex Anylander Hiking Shoes",
      productImage:
        "/assets/images/products/adidas/terrex-anylander-hiking-shoes.jpg",
      condition: "Fair",
      conditionScore: 5.2,
      grade: "C",
      defects: [
        "Worn tread pattern",
        "Discoloration on upper mesh",
        "Frayed laces",
      ],
      estimatedResaleValue: 22,
      vlmConfidence: 0.84,
      verdict: "Refurbish",
    },
  ],
  adi_pkg_007: [
    {
      productName: "Anthony Edwards 2 Shoes",
      productImage:
        "/assets/images/products/adidas/anthony-edwards-2-shoes.jpg",
      condition: "Excellent",
      conditionScore: 9.0,
      grade: "A",
      defects: [],
      estimatedResaleValue: 95,
      vlmConfidence: 0.94,
      verdict: "Resale",
    },
    {
      productName: "Street Neuclassics Track Top",
      productImage:
        "/assets/images/products/adidas/street-neuclassics-track-top.jpg",
      condition: "Excellent",
      conditionScore: 8.8,
      grade: "A",
      defects: [],
      estimatedResaleValue: 55,
      vlmConfidence: 0.93,
      verdict: "Resale",
    },
    {
      productName: "Originals Canvas Cargo Workpant",
      productImage:
        "/assets/images/products/adidas/originals-canvas-cargo-workpant.jpg",
      condition: "Good",
      conditionScore: 7.2,
      grade: "B",
      defects: ["Faded knee area"],
      estimatedResaleValue: 32,
      vlmConfidence: 0.87,
      verdict: "Resale",
    },
    {
      productName: "Adicolor Cuff Beanie",
      productImage: "/assets/images/products/adidas/adicolor-cuff-beanie.jpg",
      condition: "Good",
      conditionScore: 7.8,
      grade: "B+",
      defects: ["Slight pilling"],
      estimatedResaleValue: 15,
      vlmConfidence: 0.9,
      verdict: "Resale",
    },
    {
      productName: "Helionic Down Jacket",
      productImage:
        "/assets/images/products/adidas/helionic-climawarm-hooded-down-jacket.jpg",
      condition: "Fair",
      conditionScore: 5.5,
      grade: "C",
      defects: ["Small tear near cuff", "Stain on front panel"],
      estimatedResaleValue: 28,
      vlmConfidence: 0.82,
      verdict: "Refurbish",
    },
    {
      productName: "Predator Training Goalkeeper Gloves",
      productImage:
        "/assets/images/products/adidas/predator-training-goalkeeper-gloves.jpg",
      condition: "Fair",
      conditionScore: 4.8,
      grade: "C",
      defects: ["Worn palm grip", "Velcro weakened"],
      estimatedResaleValue: 8,
      vlmConfidence: 0.79,
      verdict: "Refurbish",
    },
    {
      productName: "Terrex Snowpitch Hiking Shoes",
      productImage:
        "/assets/images/products/adidas/terrex-snowpitch-cold.rdy-hiking-shoes.jpg",
      condition: "Good",
      conditionScore: 7.0,
      grade: "B",
      defects: ["Minor mud staining"],
      estimatedResaleValue: 52,
      vlmConfidence: 0.86,
      verdict: "Resale",
    },
    {
      productName: "Essentials Linear Backpack",
      productImage:
        "/assets/images/products/adidas/essentials-linear-backpack.jpg",
      condition: "Poor",
      conditionScore: 3.2,
      grade: "D",
      defects: ["Broken zipper", "Torn shoulder strap", "Heavy discoloration"],
      estimatedResaleValue: 0,
      vlmConfidence: 0.91,
      verdict: "Recycle",
    },
    {
      productName: "Tiro League Shin Guards",
      productImage:
        "/assets/images/products/adidas/tiro-league-shin-guards.jpg",
      condition: "Good",
      conditionScore: 6.8,
      grade: "B",
      defects: ["Surface scratches"],
      estimatedResaleValue: 10,
      vlmConfidence: 0.85,
      verdict: "Resale",
    },
  ],
  adi_pkg_008: [
    {
      productName: "Adidas Z.N.E. Puffer Jacket",
      productImage:
        "/assets/images/products/adidas/adidas-z.n.e.-puffer-climawarm-down-jacket.jpg",
      condition: "Excellent",
      conditionScore: 9.5,
      grade: "A+",
      defects: [],
      estimatedResaleValue: 120,
      vlmConfidence: 0.97,
      verdict: "Resale",
    },
    {
      productName: "Originals Workwear Canvas Jacket",
      productImage:
        "/assets/images/products/adidas/originals-workwear-canvas-jacket.jpg",
      condition: "Good",
      conditionScore: 7.3,
      grade: "B+",
      defects: ["Minor button tarnish"],
      estimatedResaleValue: 48,
      vlmConfidence: 0.89,
      verdict: "Resale",
    },
    {
      productName: "Dugout Coaches Jacket",
      productImage:
        "/assets/images/products/adidas/dugout-coaches-jacket-gender-neutral.jpg",
      condition: "Excellent",
      conditionScore: 8.6,
      grade: "A",
      defects: [],
      estimatedResaleValue: 65,
      vlmConfidence: 0.93,
      verdict: "Resale",
    },
  ],
};

const DEMO_PACKAGE_LOOKUP: Record<string, WarehousePackage> = {
  ...buildLookup(NESPRESSO_LIST, "nespresso"),
  ...buildLookup(ADIDAS_LIST, "adidas"),
};

function WarehousePackageDetailPage() {
  const { id } = Route.useParams();
  const { currentTenant } = useTenant();
  const [pkg, setPkg] = useState<WarehousePackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "grading">("details");

  const isAdidas = currentTenant.id === "adidas";
  const labels = getTenantLabels(currentTenant.id);

  const gradedItems = useMemo(() => {
    if (!pkg || !isAdidas) return null;
    return ADIDAS_GRADED_ITEMS[pkg.id] || null;
  }, [pkg, isAdidas]);

  const hasGrading = gradedItems !== null && pkg?.current_stage === "processed";

  useEffect(() => {
    loadPackage();
  }, [id, currentTenant.id]);

  const loadPackage = async () => {
    setLoading(true);

    // Use demo data directly if package exists in the demo lookup
    if (DEMO_PACKAGE_LOOKUP[id]) {
      setPkg(DEMO_PACKAGE_LOOKUP[id]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/v1/warehouse/packages/${id}`, {
        headers: {
          "X-Tenant-ID": currentTenant.id,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setPkg(result.data);
        } else {
          setPkg(getDemoPackage(id, currentTenant.id));
        }
      } else {
        setPkg(getDemoPackage(id, currentTenant.id));
      }
    } catch (err) {
      console.error("Failed to load package:", err);
      setPkg(getDemoPackage(id, currentTenant.id));
    } finally {
      setLoading(false);
    }
  };

  const getDemoPackage = (
    packageId: string,
    tenantId: string,
  ): WarehousePackage => {
    const match = DEMO_PACKAGE_LOOKUP[packageId];
    if (match) {
      return match;
    }
    const isAdi = tenantId === "adidas";
    return {
      id: packageId,
      bag_label_id: isAdi ? "ADI-2026-000000" : "NES-2025-001234",
      tenant_id: tenantId,
      customer_id: "cust_001",
      customer_name: isAdi ? "Demo Customer" : "Marie Laurent",
      customer_email: isAdi ? "demo@email.com" : "marie.laurent@email.com",
      weight_grams: 2400,
      estimated_pod_count: isAdi ? 4 : 48,
      actual_pod_count: null,
      current_stage: "received",
      kiosk_location: isAdi
        ? "adidas Flagship Store"
        : "Nespresso Boutique Champs-Elysees",
      received_at: new Date().toISOString(),
      processing_started_at: null,
      processed_at: null,
      operator_id: null,
      notes: null,
      flags: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      transitions: [
        {
          id: "trans_001",
          from_stage: null,
          to_stage: "received",
          transitioned_at: new Date().toISOString(),
          operator_id: null,
          notes: "Package received at warehouse",
        },
      ],
    };
  };

  const handleProcessingStarted = (updatedPackage: {
    id: string;
    bag_label_id: string;
    current_stage: string;
    [key: string]: unknown;
  }) => {
    setPkg((prev) => (prev ? { ...prev, ...updatedPackage } : null));
    loadPackage();
  };

  if (loading) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <div
          className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
          style={{ borderColor: "#00FBA9" }}
        />
        <p style={{ color: "#A9A9A9" }}>Loading package details...</p>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <p className="text-lg mb-4" style={{ color: "#FF4C00" }}>
          {error || "Package not found"}
        </p>
        <Link to="/warehouse" className="text-sm" style={{ color: "#00FBA9" }}>
          Back to Warehouse
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/warehouse"
          className="text-sm flex items-center gap-1 mb-4"
          style={{ color: "#A9A9A9" }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M15 18L9 12L15 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Warehouse
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-2xl font-light mb-1"
              style={{ color: "#171717" }}
            >
              Package {pkg.bag_label_id}
            </h1>
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              {pkg.kiosk_location || "Unknown location"} •{" "}
              {formatDateTime(pkg.received_at)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StageBadge stage={pkg.current_stage} />
            <StartProcessingButton
              packageId={pkg.id}
              currentStage={pkg.current_stage}
              onSuccess={handleProcessingStarted}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="Weight"
          value={pkg.weight_grams ? `${pkg.weight_grams}g` : "\u2014"}
        />
        <SummaryCard
          label={labels.estimatedCount}
          value={pkg.estimated_pod_count?.toString() || "\u2014"}
        />
        <SummaryCard
          label={labels.actualCount}
          value={pkg.actual_pod_count?.toString() || "\u2014"}
          highlight={pkg.current_stage === "Processed"}
        />
        <SummaryCard
          label="Variance"
          value={
            pkg.actual_pod_count && pkg.estimated_pod_count
              ? `${pkg.actual_pod_count - pkg.estimated_pod_count > 0 ? "+" : ""}${pkg.actual_pod_count - pkg.estimated_pod_count}`
              : "\u2014"
          }
          highlight={Boolean(
            pkg.actual_pod_count &&
            pkg.estimated_pod_count &&
            Math.abs(pkg.actual_pod_count - pkg.estimated_pod_count) /
              pkg.estimated_pod_count >
              0.2,
          )}
          warning={Boolean(
            pkg.actual_pod_count &&
            pkg.estimated_pod_count &&
            Math.abs(pkg.actual_pod_count - pkg.estimated_pod_count) /
              pkg.estimated_pod_count >
              0.2,
          )}
        />
      </div>

      {/* Tabs */}
      {hasGrading && (
        <div
          className="flex gap-0 mb-6 border-b"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <button
            onClick={() => setActiveTab("details")}
            className="px-5 py-3 text-sm font-medium transition-colors"
            style={{
              color: activeTab === "details" ? "#171717" : "#A9A9A9",
              borderBottom:
                activeTab === "details"
                  ? "2px solid #00FBA9"
                  : "2px solid transparent",
            }}
          >
            Package Details
          </button>
          <button
            onClick={() => setActiveTab("grading")}
            className="px-5 py-3 text-sm font-medium transition-colors"
            style={{
              color: activeTab === "grading" ? "#171717" : "#A9A9A9",
              borderBottom:
                activeTab === "grading"
                  ? "2px solid #00FBA9"
                  : "2px solid transparent",
            }}
          >
            Product Grading
            <span
              className="ml-2 px-1.5 py-0.5 rounded-full text-xs"
              style={{
                backgroundColor: "rgba(0,251,169,0.15)",
                color: "#00FBA9",
              }}
            >
              {gradedItems!.length}
            </span>
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "grading" && hasGrading ? (
        <ProductGradingTab items={gradedItems!} />
      ) : (
        /* Main Content — Package Details */
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Package Info & Timeline */}
          <div className="col-span-2">
            {/* Package Details */}
            <div
              className="bg-white border mb-6"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Package Details
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow label="Bag Label ID" value={pkg.bag_label_id} mono />
                  <InfoRow label="Current Stage" value={pkg.current_stage} />
                  <InfoRow
                    label={labels.locationLabel}
                    value={pkg.kiosk_location || "Unknown"}
                  />
                  <InfoRow
                    label="Weight"
                    value={pkg.weight_grams ? `${pkg.weight_grams}g` : "\u2014"}
                  />
                  <InfoRow
                    label={labels.estimatedCountLabel}
                    value={pkg.estimated_pod_count?.toString() || "\u2014"}
                  />
                  <InfoRow
                    label={labels.actualCountLabel}
                    value={pkg.actual_pod_count?.toString() || "\u2014"}
                  />
                  <InfoRow
                    label="Operator"
                    value={pkg.operator_id || "\u2014"}
                  />
                  {pkg.notes && (
                    <div className="col-span-2">
                      <InfoRow label="Notes" value={pkg.notes} />
                    </div>
                  )}
                  {pkg.flags && (
                    <div className="col-span-2">
                      <InfoRow label="Flags" value={pkg.flags} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Processing Timeline */}
            <div
              className="bg-white border mb-6"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Processing Timeline
                </h2>
              </div>
              <div className="px-6 py-4">
                {pkg.transitions && pkg.transitions.length > 0 ? (
                  <div className="space-y-4">
                    {pkg.transitions.map((transition, idx) => (
                      <div
                        key={transition.id}
                        className="flex items-start gap-4"
                      >
                        <div className="flex flex-col items-center">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: getStageColor(
                                transition.to_stage,
                              ),
                            }}
                          />
                          {idx < pkg.transitions!.length - 1 && (
                            <div
                              className="w-0.5 h-8 mt-1"
                              style={{
                                backgroundColor: "rgba(169,169,169,0.2)",
                              }}
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: "#171717" }}
                          >
                            {transition.from_stage
                              ? `${transition.from_stage} \u2192 ${transition.to_stage}`
                              : transition.to_stage}
                          </p>
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {formatDateTime(transition.transitioned_at)}
                            {transition.operator_id &&
                              ` \u2022 ${transition.operator_id}`}
                          </p>
                          {transition.notes && (
                            <p
                              className="text-xs mt-1"
                              style={{ color: "#A9A9A9" }}
                            >
                              {transition.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "#A9A9A9" }}>No transitions recorded</p>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div
              className="bg-white border"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Timestamps
                </h2>
              </div>
              <div className="px-6 py-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>
                    Received
                  </span>
                  <span className="text-sm" style={{ color: "#171717" }}>
                    {formatDateTime(pkg.received_at)}
                  </span>
                </div>
                {pkg.processing_started_at && (
                  <div className="flex justify-between">
                    <span className="text-xs" style={{ color: "#A9A9A9" }}>
                      Processing Started
                    </span>
                    <span className="text-sm" style={{ color: "#171717" }}>
                      {formatDateTime(pkg.processing_started_at)}
                    </span>
                  </div>
                )}
                {pkg.processed_at && (
                  <div className="flex justify-between">
                    <span className="text-xs" style={{ color: "#A9A9A9" }}>
                      Processing Completed
                    </span>
                    <span className="text-sm" style={{ color: "#171717" }}>
                      {formatDateTime(pkg.processed_at)}
                    </span>
                  </div>
                )}
                {pkg.processing_started_at && (
                  <div className="flex justify-between">
                    <span className="text-xs" style={{ color: "#A9A9A9" }}>
                      Time in Processing
                    </span>
                    <span className="text-sm" style={{ color: "#171717" }}>
                      {pkg.processed_at
                        ? formatDuration(
                            pkg.processing_started_at,
                            pkg.processed_at,
                          )
                        : formatDuration(
                            pkg.processing_started_at,
                            new Date().toISOString(),
                          ) + " (ongoing)"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Customer Info */}
          <div>
            {/* Customer Card */}
            <div
              className="bg-white border mb-6"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Customer
                </h2>
              </div>
              <div className="px-6 py-4">
                {pkg.customer_name ? (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#FAFAFA" }}
                      >
                        <span className="text-lg" style={{ color: "#171717" }}>
                          {pkg.customer_name[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: "#171717" }}>
                          {pkg.customer_name}
                        </p>
                        {pkg.customer_email && (
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {pkg.customer_email}
                          </p>
                        )}
                      </div>
                    </div>
                    {pkg.customer_id && (
                      <div className="text-xs" style={{ color: "#A9A9A9" }}>
                        Customer ID: {pkg.customer_id}
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ color: "#A9A9A9" }}>No customer information</p>
                )}
              </div>
            </div>

            {/* Package ID Card */}
            <div
              className="bg-white border"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Package ID
                </h2>
              </div>
              <div className="px-6 py-4">
                <div
                  className="p-4 text-center font-mono text-lg"
                  style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
                >
                  {pkg.bag_label_id}
                </div>
                <p
                  className="text-xs text-center mt-2"
                  style={{ color: "#A9A9A9" }}
                >
                  Internal ID: {pkg.id}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Product Grading Tab
function ProductGradingTab({ items }: { items: GradedItem[] }) {
  const totalItems = items.length;
  const avgScore =
    items.reduce((sum, i) => sum + i.conditionScore, 0) / totalItems;
  const resaleCount = items.filter((i) => i.verdict === "Resale").length;
  const refurbishCount = items.filter((i) => i.verdict === "Refurbish").length;
  const recycleCount = items.filter((i) => i.verdict === "Recycle").length;
  const totalResaleValue = items.reduce(
    (sum, i) => sum + i.estimatedResaleValue,
    0,
  );

  return (
    <div>
      {/* Summary Bar */}
      <div
        className="bg-white border p-5 mb-6 grid grid-cols-5 gap-4"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Items Scanned
          </p>
          <p className="text-xl font-light" style={{ color: "#171717" }}>
            {totalItems}
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Avg. Score
          </p>
          <p className="text-xl font-light" style={{ color: "#171717" }}>
            {avgScore.toFixed(1)}/10
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Resale
          </p>
          <p className="text-xl font-light" style={{ color: "#00FBA9" }}>
            {resaleCount}
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Refurbish
          </p>
          <p className="text-xl font-light" style={{ color: "#F5A623" }}>
            {refurbishCount}
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Recycle
          </p>
          <p className="text-xl font-light" style={{ color: "#A9A9A9" }}>
            {recycleCount}
          </p>
        </div>
      </div>

      {/* Total Resale Value */}
      <div
        className="bg-white border p-4 mb-6 flex items-center justify-between"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        <span className="text-sm" style={{ color: "#A9A9A9" }}>
          Total Estimated Resale Value
        </span>
        <span className="text-lg font-medium" style={{ color: "#171717" }}>
          ${totalResaleValue}
        </span>
      </div>

      {/* Item Cards */}
      <div className="space-y-4">
        {items.map((item, idx) => (
          <GradedItemCard key={idx} item={item} />
        ))}
      </div>
    </div>
  );
}

function GradedItemCard({ item }: { item: GradedItem }) {
  const verdictColors: Record<string, { bg: string; text: string }> = {
    Resale: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    Refurbish: { bg: "rgba(245,166,35,0.15)", text: "#F5A623" },
    Recycle: { bg: "rgba(169,169,169,0.15)", text: "#A9A9A9" },
  };

  const conditionColors: Record<string, string> = {
    Excellent: "#00FBA9",
    Good: "#3941EB",
    Fair: "#F5A623",
    Poor: "#FF4C00",
  };

  const gradeColor =
    item.conditionScore >= 8
      ? "#00FBA9"
      : item.conditionScore >= 6
        ? "#3941EB"
        : item.conditionScore >= 4
          ? "#F5A623"
          : "#FF4C00";

  const vc = verdictColors[item.verdict] || verdictColors.Recycle;

  return (
    <div
      className="bg-white border p-4 flex gap-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      {/* Product Image */}
      <div className="w-24 h-24 flex-shrink-0 bg-gray-50 overflow-hidden">
        <img
          src={item.productImage}
          alt={item.productName}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: name + condition badge */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3
            className="text-sm font-medium truncate"
            style={{ color: "#171717" }}
          >
            {item.productName}
          </h3>
          <span
            className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
            style={{
              backgroundColor: `${conditionColors[item.condition]}20`,
              color: conditionColors[item.condition],
            }}
          >
            {item.condition}
          </span>
        </div>

        {/* Grade + Score + Value row */}
        <div className="flex items-center gap-4 mb-2">
          {/* Grade circle */}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: `${gradeColor}15`,
              border: `2px solid ${gradeColor}`,
            }}
          >
            <span className="text-sm font-bold" style={{ color: gradeColor }}>
              {item.grade}
            </span>
          </div>

          {/* Score bar */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs" style={{ color: "#A9A9A9" }}>
                Score
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: "#171717" }}
              >
                {item.conditionScore}/10
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ backgroundColor: "rgba(169,169,169,0.15)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(item.conditionScore / 10) * 100}%`,
                  backgroundColor: gradeColor,
                }}
              />
            </div>
          </div>

          {/* Resale value */}
          {item.estimatedResaleValue > 0 && (
            <div className="text-right flex-shrink-0">
              <p className="text-xs" style={{ color: "#A9A9A9" }}>
                Est. Resale
              </p>
              <p className="text-sm font-medium" style={{ color: "#171717" }}>
                ${item.estimatedResaleValue}
              </p>
            </div>
          )}
        </div>

        {/* Bottom row: defects + verdict */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {item.defects.map((defect, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded text-xs"
                style={{ backgroundColor: "#FEF3C7", color: "#D97706" }}
              >
                {defect}
              </span>
            ))}
            {item.defects.length === 0 && (
              <span className="text-xs" style={{ color: "#A9A9A9" }}>
                No defects
              </span>
            )}
          </div>
          <span
            className="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
            style={{ backgroundColor: vc.bg, color: vc.text }}
          >
            {item.verdict}
          </span>
        </div>

        {/* VLM confidence */}
        <div className="mt-2">
          <span className="text-xs" style={{ color: "#A9A9A9" }}>
            VLM Confidence: {(item.vlmConfidence * 100).toFixed(0)}%
          </span>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  highlight,
  warning,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  warning?: boolean;
}) {
  return (
    <div
      className="bg-white border p-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <p
        className="text-xs uppercase tracking-wider mb-2"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="text-2xl font-light"
        style={{
          color: warning ? "#FF4C00" : highlight ? "#00FBA9" : "#171717",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <p
        className={`text-sm ${mono ? "font-mono" : ""}`}
        style={{ color: "#171717" }}
      >
        {value}
      </p>
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    Received: { bg: "rgba(57,65,235,0.15)", color: "#3941EB" },
    Processing: { bg: "rgba(255,193,7,0.15)", color: "#FFC107" },
    Processed: { bg: "rgba(0,251,169,0.15)", color: "#00FBA9" },
    Rejected: { bg: "rgba(255,76,0,0.15)", color: "#FF4C00" },
  };
  const style = styles[stage] || styles.Received;

  return (
    <span
      className="px-3 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {stage}
    </span>
  );
}

function getStageColor(stage: string): string {
  const colors: Record<string, string> = {
    Received: "#3941EB",
    Processing: "#FFC107",
    Processed: "#00FBA9",
    Rejected: "#FF4C00",
  };
  return colors[stage] || "#A9A9A9";
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) {
    return `${diffMins} min`;
  }
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return `${hours}h ${mins}m`;
}
